import { SelectOption } from '@globaleris/ui.library'
import { CicloFacturacion, EnumTipoCicloFacturacion } from '../../..'
import moment from 'moment'
import { AppConfigurationKey } from '../../../../model/enums/appConfigurationKey'
import { getAppConfigurationValue } from '../../../../redux'
import AxiosInstance from '../../../http-config'
import { sanitizeQuery } from '../../../../helpers/sanitizeQueryHelper'
import {
  AddTradeAPIRequest,
  AddTradeRequest,
  CheckCounterpartTradeRulebookRequest,
  CheckEndDateTractRulebookRequest,
  CheckEndDateTradeRulebookRequest,
  CheckLogisticElementRulebookRequest,
  CheckQuantityRulebookRequest,
  CheckStartDateTractRulebookRequest,
  CheckStartDateTradeRulebookRequest,
  CheckTradeSimpleResponse,
  FetchAPIRequest,
  FetchRequest,
  PatchTradePioneerRequest,
  RulebookResponse,
  TradeDetailsResponse,
  TradeListItem,
  ValidatePositionRulebookRequest,
  ValidateTractRulebookRequest,
  ValidateTradeRulebookRequest,
  GeneratePositionCodeAPIRequest,
  GeneratePositionCodeAPIResponse,
  CheckPositionTypeRulebookRequest,
  CheckDateTimeLessThanRulebookRequest,
  CheckPastTradeRulebookRequest,
  CheckDeletePositionRulebookRequest,
  PriceConceptRequest,
  CreatePriceForPositionRequest,
  CreatePriceForPositionResponse,
  CheckEndDatePositionRulebookRequest,
  CheckDeleteTractRulebookRequest,
  CheckPresentFutureDatesRulebookRequest
} from '../../model/trades'
import { Currency } from '../../model/Currency'
import { MeasureUnit } from '../../model/MeasureUnit'
import { UnitPrice } from '../../model/UnitPrice'
import Axios from 'axios'

export let cancelTokenSource: (() => void) | null = null
export class ApiTrades {
  //#region Fetch

  Fetch = async (request: FetchRequest): Promise<TradeListItem[]> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    let url = `/api/trades?company=${request.company}&balanceZone=${request.balanceZone}&version=3`

    if (request.initDate) {
      url = url + `&initDate=${moment(request.initDate).format('YYYY-MM-DD')}`
    }

    if (request.endDate) {
      url = url + `&endDate=${moment(request.endDate).format('YYYY-MM-DD')}`
    }

    if (request.counterpart) {
      url = url + `&counterpart=${request.counterpart}`
    }

    if (request.deliveryPoint) {
      url = url + `&deliveryPoint=${request.deliveryPoint}`
    }

    return await AxiosInstance.get(`${apiSuffixUrl}${url}`).then(response => {
      if (response.data.length > 0) {
        return response.data
      } else {
        return []
      }
    })
  }

  FetchActives = async (request: FetchAPIRequest): Promise<TradeListItem[]> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      // `https://localhost:52763/api/trades/Actives`,
      `${apiSuffixUrl}/api/trades/Actives`,
      request
    ).then(response => {
      if (response.data.length > 0) {
        return response.data
      } else {
        return []
      }
    })
  }

  FetchActivesAndFutures = async (request: FetchAPIRequest): Promise<TradeListItem[]> => {
    return await AxiosInstance.post(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/trades/ActivesAndFutures`,
      request
    ).then(response => {
      if (response.data.length > 0) {
        return response.data
      } else {
        return []
      }
    })
  }

  FetchCustom = async (request: FetchAPIRequest): Promise<TradeListItem[]> => {
    return await AxiosInstance.post(
      `${getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)}/api/trades/Custom`,
      request,
      { cancelToken: new Axios.CancelToken(c => (cancelTokenSource = c)) }
    ).then(response => {
      if (response.data.length > 0) {
        return response.data
      } else {
        return []
      }
    })
  }

  FetchCustomCount = async (request: FetchRequest): Promise<number> => {
    return await AxiosInstance.post(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/trades/FetchCustomCount`,
      request
    ).then(response => {
      return response.data
    })
  }

  FetchTradeTypes = async (): Promise<SelectOption[]> => {
    return await AxiosInstance.get(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/trades/GetTradeTypes`
    ).then(response => {
      return response.data
    })
  }

  FetchBooks = async (): Promise<SelectOption[]> => {
    return await AxiosInstance.get(
      `${getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)}/api/trades/GetBooks`
    ).then(response => {
      return response.data
    })
  }

  FetchContractedProducts = async (idBroker: number | string): Promise<SelectOption[]> => {
    return await AxiosInstance.get(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/trades/GetContractedProducts?broker=${idBroker}`
    ).then(response => {
      return response.data
    })
  }

  FetchCurrencies = async (): Promise<Currency[]> => {
    return await AxiosInstance.get(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/Currencies/GetCurrencies`
    ).then(response => {
      return response.data
    })
  }

  FetchMeasureUnits = async (): Promise<MeasureUnit[]> => {
    return await AxiosInstance.get(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/MeasureUnits/GetMeasureUnits`
    ).then(response => {
      return response.data
    })
  }

  FetchUnitPricesByType = async (unitType: number): Promise<UnitPrice[]> => {
    return await AxiosInstance.get(
      `${getAppConfigurationValue(
        AppConfigurationKey.ApiUrlSuffixPioneer
      )}/api/UnitPrices/FetchUnitPricesByUnitType?unitType=${unitType}`
    ).then(response => {
      return response.data
    })
  }

  //#endregion
  // Trades Operations

  CreateTrade = async (request: AddTradeAPIRequest): Promise<void> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)
    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CreateTrade?version=3.0`,
      request
    ).then(response => {
      return response.data
    })
  }

  GeneratePositionCode = async (
    request: GeneratePositionCodeAPIRequest
  ): Promise<GeneratePositionCodeAPIResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)
    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/GeneratePositionCode`,
      request
    ).then(response => {
      return response.data
    })
  }

  DeleteTrade = async (tradeId: number): Promise<any> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.delete(`${apiSuffixUrl}/api/trades/${tradeId}`).then(response => {
      return response.data
    })
  }

  GetTradeById = async (tradeId: number): Promise<TradeDetailsResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.get(`${apiSuffixUrl}/api/trades/${tradeId}`).then(response => {
      return response.data
    })
  }

  PatchTrade = async (tradeId: number, request: PatchTradePioneerRequest): Promise<any> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.patch(`${apiSuffixUrl}/api/trades/${tradeId}`, request).then(
      response => {
        return response.data
      }
    )
  }

  CheckTradeSimple = async (tradeId: number): Promise<CheckTradeSimpleResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.get(`${apiSuffixUrl}/api/trades/CheckTradeSimple/${tradeId}`).then(
      response => {
        return response.data
      }
    )
  }

  CreatePriceForPosition = async (
    request: CreatePriceForPositionRequest
  ): Promise<CreatePriceForPositionResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)
    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CreatePriceForPosition`,
      request
    ).then(response => {
      return response.data
    })
  }
  // Rulebook

  CheckCounterpartTradeRulebook = async (
    request: CheckCounterpartTradeRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckCounterpartTradeRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckStartDateTradeRulebook = async (
    request: CheckStartDateTradeRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckStartDateTradeRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckEndDateTradeRulebook = async (
    request: CheckEndDateTradeRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckEndDateTradeRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckEndDatePositionRulebook = async (
    request: CheckEndDatePositionRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckEndDatePositionRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckStartDateTractRulebook = async (
    request: CheckStartDateTractRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckStartDateTractRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckEndDateTractRulebook = async (
    request: CheckEndDateTractRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckEndDateTractRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckLogisticElementRulebook = async (
    request: CheckLogisticElementRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckLogisticElementRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckQuantityRulebook = async (
    request: CheckQuantityRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)
    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckQuantityRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckPositionTypeRulebook = async (
    request: CheckPositionTypeRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckPositionTypeRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckPresentFutureDatesRulebook = async (
    request: CheckPresentFutureDatesRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckPresentFutureDatesRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckDeletePositionRulebook = async (
    request: CheckDeletePositionRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckDeletePositionRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  CheckDeleteTractRulebook = async (
    request: CheckDeleteTractRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckDeleteTractRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  ValidateTradeRulebook = async (
    request: ValidateTradeRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/ValidateTradeRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  ValidatePositionRulebook = async (
    request: ValidatePositionRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/ValidatePositionRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  ValidateTractRulebook = async (
    request: ValidateTractRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/ValidateTractRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  // Checks if provided date is before than system's current gas day.
  CheckDateTimeLessThanRulebook = async (
    request: CheckDateTimeLessThanRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckDateTimeLessThanRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }

  // Checks if counterpart/subject subtype is valid to be created within a Trade with past dates (subtype MERCADO or MAYORISTA can't be and will return not valid response)
  CheckCreatePastTradeRulebook = async (
    request: CheckPastTradeRulebookRequest
  ): Promise<RulebookResponse> => {
    const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

    return await AxiosInstance.post(
      `${apiSuffixUrl}/api/trades/CheckPastTradeRulebook`,
      request
    ).then(response => {
      return response.data
    })
  }
}

// Get codTrades for searchfieldInput (Buy and Sell)
export const apiGetTradesByCode = async (query: string): Promise<TradeListItem[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.ApiUrlSuffixPioneer)

  // Sanitize query to prevent SQL injections
  const sanitizedQuery = sanitizeQuery(query)

  // Skip API call if query is empty after sanitization
  if (!sanitizedQuery) {
    return []
  }

  // Axios params handles URL encoding automatically for special characters (ñ, á, spaces, etc.)
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Trades/GetTradesByCode`, {
    params: { query: sanitizedQuery }
  }).then(response => {
    if (response.data.length > 0) {
      return response.data
    } else {
      return []
    }
  })
}
